import { Component, OnInit } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { Router } from '@angular/router';
import { JobWithPurchaseOrdersDto } from '@app/models/job-with-purchase-orders.model';
import { InvoiceService } from '@app/services/invoice/invoice.service';
import { NavigationService } from '@app/services/navigation/navigation.service';

interface InvoiceFilters {
  jobType: string;
  jobNumber: string;
  siteName: string;
}

@Component({
  selector: 'app-jobs-awaiting-invoice',
  templateUrl: './jobs-awaiting-invoice.component.html',
  styleUrls: ['./jobs-awaiting-invoice.component.scss'],
  standalone: false
})
export class JobsAwaitingInvoiceComponent implements OnInit {
  jobs: JobWithPurchaseOrdersDto[] = [];
  filters: InvoiceFilters = this.emptyFilters();
  appliedFilters: InvoiceFilters = this.emptyFilters();
  pageIndex = 0;
  pageSize = 10;
  hasLoaded = false;
  hasLoadError = false;

  constructor(
    private readonly invoiceService: InvoiceService,
    private readonly navigationService: NavigationService,
    private readonly router: Router
  ) {
    this.navigationService.onChange(true, false);
  }

  get jobTypes(): string[] {
    return this.uniqueValues(this.jobs.map(job => job.jobType));
  }

  get siteNames(): string[] {
    return this.uniqueValues(this.jobs.map(job => job.site?.name));
  }

  get filteredJobs(): JobWithPurchaseOrdersDto[] {
    const jobNumber = this.appliedFilters.jobNumber.trim().toLowerCase();

    return this.jobs.filter(job =>
      (!this.appliedFilters.jobType || job.jobType === this.appliedFilters.jobType) &&
      (!this.appliedFilters.siteName || job.site?.name === this.appliedFilters.siteName) &&
      (!jobNumber || this.jobSearchValues(job).some(value => value.toLowerCase().includes(jobNumber)))
    );
  }

  get pagedJobs(): JobWithPurchaseOrdersDto[] {
    const start = this.pageIndex * this.pageSize;
    return this.filteredJobs.slice(start, start + this.pageSize);
  }

  ngOnInit(): void {
    this.invoiceService.getJobsWithApprovedReceiptPurchaseOrders().subscribe({
      next: jobs => {
        this.jobs = jobs ?? [];
        this.hasLoaded = true;
      },
      error: () => {
        this.hasLoadError = true;
        this.hasLoaded = true;
      }
    });
  }

  applyFilters(): void {
    this.appliedFilters = { ...this.filters };
    this.pageIndex = 0;
  }

  changePage(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
  }

  goBack(): void {
    void this.router.navigate(['dashboard']);
  }

  submitInvoice(job: JobWithPurchaseOrdersDto): void {
    void this.router.navigate(['invoice', job.id]);
  }

  jobNumber(job: JobWithPurchaseOrdersDto): string {
    return job.referenceNumber || job.calloutReferenceNumber || job.purchaseOrders[0]?.jobNumber || '-';
  }

  purchaseOrderNumbers(job: JobWithPurchaseOrdersDto): string {
    return this.uniqueValues(job.purchaseOrders.map(purchaseOrder =>
      purchaseOrder.purchaseOrderNumber || purchaseOrder.purchaseOrderReferenceId
    )).join(', ') || '-';
  }

  private jobSearchValues(job: JobWithPurchaseOrdersDto): string[] {
    return [
      job.referenceNumber,
      job.calloutReferenceNumber,
      ...job.purchaseOrders.flatMap(purchaseOrder => [purchaseOrder.jobNumber, purchaseOrder.jobReferenceId])
    ].filter((value): value is string => Boolean(value));
  }

  private uniqueValues(values: (string | undefined)[]): string[] {
    return [...new Set(values.filter((value): value is string => Boolean(value)))].sort();
  }

  private emptyFilters(): InvoiceFilters {
    return { jobType: '', jobNumber: '', siteName: '' };
  }
}